import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack
} from '@chakra-ui/react';

const API_HEADERS = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const formatDate = (value) => {
  if (!value) return 'Nunca';
  return new Date(value).toLocaleString('pt-BR');
};

const ApiKeysTab = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createdKey, setCreatedKey] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/api-keys', { headers: API_HEADERS() });
      if (response.status === 403) {
        setKeys([]);
        return;
      }
      if (!response.ok) throw new Error('Erro ao carregar chaves de API');
      const data = await response.json();
      setKeys(data.api_keys || []);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({ title: 'Informe um nome para a chave', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      const response = await fetch('/api/api-keys', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify({ name: name.trim(), description: description.trim() || null })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao criar chave');
      setCreatedKey(data.api_key);
      setName('');
      setDescription('');
      await fetchKeys();
      onOpen();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const copyKey = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Chave copiada', status: 'success', duration: 2000, isClosable: true });
    } catch {
      toast({ title: 'Não foi possível copiar', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const toggleKey = async (key) => {
    try {
      const response = await fetch(`/api/api-keys/${key.id}`, {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({ is_active: !key.is_active })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao atualizar chave');
      setKeys((prev) => prev.map((item) => (item.id === key.id ? data.api_key : item)));
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const revokeKey = async (key) => {
    if (!window.confirm(`Revogar a chave "${key.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      const response = await fetch(`/api/api-keys/${key.id}`, {
        method: 'DELETE',
        headers: API_HEADERS()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao revogar chave');
      setKeys((prev) => prev.filter((item) => item.id !== key.id));
      toast({ title: 'Chave revogada', status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  return (
    <Box>
      <Heading size="md" mb={2}>API de consumo de infrações</Heading>
      <Text color="gray.600" mb={4}>
        Consulte infrações por sistemas externos usando uma chave de API. A chave é enviada no header
        {' '}<Code>X-API-Key</Code> ou <Code>Authorization: ApiKey &lt;chave&gt;</Code>.
      </Text>

      <Alert status="info" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box>
          <AlertTitle fontSize="sm">Endpoints</AlertTitle>
          <AlertDescription fontSize="sm">
            <Text><Code>GET /api/v1/infractions</Code> — listar, com filtros</Text>
            <Text><Code>GET /api/v1/infractions/:id</Code> — detalhe</Text>
            <Text><Code>GET /api/v1/infractions/:id/image</Code> — imagem</Text>
            <Text mt={2}>
              Filtros: <Code>plate</Code>, <Code>status</Code>, <Code>camera_id</Code>, <Code>algorithm_id</Code>,
              {' '}<Code>infraction_class</Code>, <Code>start_date</Code>, <Code>end_date</Code>, <Code>updated_since</Code>,
              {' '}<Code>page</Code>, <Code>limit</Code>
            </Text>
          </AlertDescription>
        </Box>
      </Alert>

      <VStack align="stretch" spacing={4} mb={6} maxW="520px">
        <FormControl>
          <FormLabel>Nome da chave</FormLabel>
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Integração DETRAN" />
        </FormControl>
        <FormControl>
          <FormLabel>Descrição</FormLabel>
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Sistema externo que consulta infrações confirmadas"
            rows={2}
          />
          <FormHelperText>A chave completa aparece uma única vez, no momento da criação.</FormHelperText>
        </FormControl>
        <Button colorScheme="teal" onClick={handleCreate} isLoading={saving} alignSelf="flex-start">
          Gerar chave
        </Button>
      </VStack>

      <Divider mb={4} />

      {loading ? (
        <Text color="gray.500">Carregando chaves...</Text>
      ) : keys.length === 0 ? (
        <Text color="gray.500">Nenhuma chave cadastrada.</Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Prefixo</Th>
                <Th>Status</Th>
                <Th>Último uso</Th>
                <Th>Criada em</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {keys.map((key) => (
                <Tr key={key.id}>
                  <Td>
                    <Text fontWeight="medium">{key.name}</Text>
                    {key.description && <Text fontSize="xs" color="gray.500">{key.description}</Text>}
                  </Td>
                  <Td><Code>{key.key_prefix}…</Code></Td>
                  <Td>
                    <Badge colorScheme={key.is_active ? 'green' : 'gray'}>
                      {key.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </Td>
                  <Td>{formatDate(key.last_used_at)}</Td>
                  <Td>{formatDate(key.created_at)}</Td>
                  <Td>
                    <HStack>
                      <Switch isChecked={key.is_active} onChange={() => toggleKey(key)} />
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => revokeKey(key)}>
                        Revogar
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Chave criada</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              Copie a chave agora. Por segurança, ela não será exibida novamente.
            </Alert>
            <Code display="block" p={3} borderRadius="md" whiteSpace="pre-wrap">
              {createdKey?.key}
            </Code>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => copyKey(createdKey?.key || '')} colorScheme="teal">
              Copiar
            </Button>
            <Button variant="ghost" onClick={onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ApiKeysTab;
